import { useState } from 'react'

import type { AnioFlujo } from '../api'
import { usd } from '../formato'

/**
 * Flujo de caja ACUMULADO, año a año: la pregunta "¿cuando recupero lo invertido?".
 *
 * Una sola serie con polaridad: bajo la linea de cero (naranja) todavia se debe la
 * inversion; encima (verde) ya es ganancia. El color refuerza lo que la POSICION ya dice,
 * por eso la pareja verde/naranja es aceptable para daltonismo (validada: ΔE 6.0 con
 * codificacion secundaria). El año de recuperacion va rotulado.
 */

const ANCHO = 720
const ALTO = 240
const M = { arriba: 16, derecha: 12, abajo: 26, izquierda: 64 }
const RADIO = 4
const HUECO = 2

function barra(x: number, ancho: number, y0: number, y1: number): string {
  // Redondeada solo en el extremo del dato, recta contra la linea de cero.
  const arriba = y1 < y0
  const alto = Math.abs(y1 - y0)
  const r = Math.min(RADIO, ancho / 2, alto)
  if (arriba) {
    return `M${x},${y0} V${y1 + r} Q${x},${y1} ${x + r},${y1} H${x + ancho - r} Q${x + ancho},${y1} ${x + ancho},${y1 + r} V${y0} Z`
  }
  return `M${x},${y0} V${y1 - r} Q${x},${y1} ${x + r},${y1} H${x + ancho - r} Q${x + ancho},${y1} ${x + ancho},${y1 - r} V${y0} Z`
}

function marcas(min: number, max: number): number[] {
  const rango = max - min || 1
  const paso0 = 10 ** Math.floor(Math.log10(rango / 4))
  const paso = [1, 2, 5, 10].map((m) => m * paso0).find((p) => rango / p <= 5) ?? paso0 * 10
  const valores: number[] = []
  for (let v = Math.ceil(min / paso) * paso; v <= max; v += paso) valores.push(v)
  return valores
}

export function GraficoFlujo({ anios, inversion, payback }: { anios: AnioFlujo[]; inversion: number; payback: number | null }) {
  const [activo, setActivo] = useState<number | null>(null)
  const serie = [{ anio: 0, valor: -inversion }, ...anios.map((a) => ({ anio: a.anio, valor: a.flujo_acumulado }))]

  const min = Math.min(0, ...serie.map((s) => s.valor))
  const max = Math.max(0, ...serie.map((s) => s.valor))
  const alto = ALTO - M.arriba - M.abajo
  const y = (v: number) => M.arriba + ((max - v) / (max - min || 1)) * alto
  const paso = (ANCHO - M.izquierda - M.derecha) / serie.length
  const anchoBarra = Math.max(2, paso - HUECO)
  const cero = y(0)
  const anioPayback = payback === null ? null : Math.ceil(payback)

  const punto = activo === null ? null : serie[activo]

  return (
    <figure className="grafico">
      <div className="grafico-lienzo">
        <svg viewBox={`0 0 ${ANCHO} ${ALTO}`} role="img" aria-label="Flujo de caja acumulado por año">
          {marcas(min, max).map((v) => (
            <g key={v}>
              <line x1={M.izquierda} x2={ANCHO - M.derecha} y1={y(v)} y2={y(v)} className="rejilla" />
              <text x={M.izquierda - 8} y={y(v)} className="eje" textAnchor="end" dominantBaseline="middle">
                {usd(v)}
              </text>
            </g>
          ))}
          {serie.map((s, i) => {
            const x = M.izquierda + i * paso + HUECO / 2
            return (
              <g key={s.anio} onMouseEnter={() => setActivo(i)} onMouseLeave={() => setActivo(null)}>
                {/* Zona de toque mas grande que la barra: se apunta a la columna, no al pixel. */}
                <rect x={x - HUECO / 2} y={M.arriba} width={paso} height={alto} fill="transparent" />
                <path
                  d={barra(x, anchoBarra, cero, y(s.valor))}
                  className={s.valor >= 0 ? 'barra-positiva' : 'barra-negativa'}
                  opacity={activo === null || activo === i ? 1 : 0.55}
                />
                {s.anio % 5 === 0 && (
                  <text x={x + anchoBarra / 2} y={ALTO - 8} className="eje" textAnchor="middle">
                    {s.anio === 0 ? 'Hoy' : `Año ${s.anio}`}
                  </text>
                )}
              </g>
            )
          })}
          <line x1={M.izquierda} x2={ANCHO - M.derecha} y1={cero} y2={cero} className="cero" />
          {anioPayback !== null && anioPayback < serie.length && (
            <text
              x={M.izquierda + anioPayback * paso + paso / 2}
              y={cero - 8}
              className="rotulo"
              textAnchor="middle"
            >
              Se recupera ▾
            </text>
          )}
        </svg>
        {punto && activo !== null && (
          <div
            className="tooltip"
            style={{ left: `${((M.izquierda + activo * paso + paso / 2) / ANCHO) * 100}%` }}
          >
            <strong>{punto.anio === 0 ? 'Inversión (hoy)' : `Año ${punto.anio}`}</strong>
            <span>Acumulado: {usd(punto.valor)}</span>
            {punto.anio > 0 && <span>Flujo del año: {usd(anios[punto.anio - 1]!.flujo_neto)}</span>}
          </div>
        )}
      </div>
      <figcaption className="suave">
        Flujo acumulado sin descontar. Bajo la línea: aún se está pagando la inversión; encima: ganancia.
      </figcaption>
    </figure>
  )
}
